import { QueryResult } from '@apollo/react-common';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { logEvent } from 'expo-firebase-analytics';
import React, { useEffect, useState } from 'react';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Redirect, useHistory } from 'react-router';
import { Route, Switch } from 'react-router-dom';

import { logo } from '../../../../assets';
import { pageTitle } from '../../../constants/pageTitle';
import { useAuth } from '../../../context';
import { logout as logout_type } from '../../../generated/logout';
import {
  VerificationRequests,
  VerificationRequestsVariables,
} from '../../../generated/VerificationRequests';
import { LOGOUT as LOGOUT_MUTATION } from '../../../graphql/mutations';
import { routePaths } from '../constants/routes';
import { colors } from '../constants/theme';
import { Button, Text } from '../core-ui';
import { defaultFilter } from '../core-ui/FilterModal';
import { VERIFICATION_REQUESTS } from '../graphql/queries';

import { NewProgramParticipantPage } from './programParticipant/newProgramParticipantPage';
import { WipParticipant } from './programParticipant/WipParticipant';
import { QueueScene } from './queue';
import { WorkInProgressScene } from './workInProgress';

export type VerificationRequestsQueryResult = {
  data: VerificationRequests;
} & Pick<
  QueryResult<VerificationRequests, VerificationRequestsVariables>,
  'loading' | 'fetchMore' | 'refetch' | 'error' | 'networkStatus'
>;

export default function TokoVerification() {
  let { name, logout, id: adminId } = useAuth();
  let history = useHistory();
  let url = history.location.pathname;
  let tab = url.split('/');
  let selectedTab = tab[tab.length - 1];

  let [filter, setFilter] = useState(defaultFilter);

  const verificationRequestsQueryResult = useQuery<
    VerificationRequests,
    VerificationRequestsVariables
  >(VERIFICATION_REQUESTS, {
    notifyOnNetworkStatusChange: true,
    variables: filter,
    skip: Object.values(filter).every((v) => !v),
    fetchPolicy: 'network-only',
  });

  let { data, refetch } = verificationRequestsQueryResult;

  let resultData = data || {
    requestVerificationAdvance: {
      __typename: 'RequestVerificationAdvance',
      row: [],
      total: 0,
    },
  };

  useEffect(() => {
    const eventLog = async () => {
      await logEvent('page_view', { page_title: pageTitle.TokoVerification });
    };
    eventLog();
  }, []);

  const [logoutCMS] = useMutation<logout_type>(LOGOUT_MUTATION);
  const onLogout = async () => {
    await logoutCMS();
    logout();
  };

  let listTab = ['list', 'wip', 'programParticipant'];

  return (
    <View style={styles.root}>
      {listTab.includes(selectedTab) ? (
        <View style={styles.header}>
          <View style={styles.headerTitle}>
            <Image source={logo} resizeMode="contain" style={styles.logo} />
            <Text bold size="l">
              {t(['Verifikasi TOKO', 'TOKO Verification'])}
            </Text>
          </View>
          <View style={styles.row}>
            <TouchableOpacity
              data-testid="list-daftar-data"
              style={[
                styles.tab,
                selectedTab === 'list' ? styles.selectedTab : {},
              ]}
              onPress={() => history.push(routePaths.list)}
            >
              <Text
                color={selectedTab === 'list' ? 'black' : 'grey'}
                bold
                size="l"
              >
                {t(['Daftar Data', 'List Of Data'])}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              data-testid="list-dalam-masa-pengerjaan"
              style={[
                styles.tab,
                selectedTab === 'wip' ? styles.selectedTab : {},
              ]}
              onPress={() => {
                refetch({
                  ...defaultFilter,
                  adminId: adminId,
                  pagination: { first: 8, skip: 0 },
                });
                history.push(routePaths.wip);
              }}
            >
              <Text
                color={selectedTab === 'wip' ? 'black' : 'grey'}
                bold
                size="l"
              >
                {t(['Dalam Masa Pengerjaan', 'Working In Progress'])}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              data-testid="list-program-participant"
              style={[
                styles.tab,
                selectedTab === 'programParticipant' ? styles.selectedTab : {},
              ]}
              onPress={() => history.push(routePaths.newProgramParticipant)}
            >
              <Text
                color={selectedTab === 'programParticipant' ? 'black' : 'grey'}
                bold
                size="l"
              >
                {t(['Program Principal', 'Program Principal'])}
              </Text>
            </TouchableOpacity>
          </View>
          <View style={styles.logout}>
            <Text size="m" style={{ borderRightWidth: 1, paddingRight: 8 }}>
              Hai,{' '}
              <Text bold size="m">
                {name}
              </Text>
            </Text>
            <Button
              data-testid="btn-logout"
              preset="transparent"
              onPress={onLogout}
              title={t(['Keluar', 'Logout'])}
              titleTextProps={{ color: 'black', size: 'm', bold: false }}
            />
          </View>
        </View>
      ) : (
        <></>
      )}
      <View style={styles.contentWrapper}>
        <Switch>
          <Route path="/" exact>
            <Redirect to={routePaths.list} />
          </Route>
          <Route path={routePaths.list} component={QueueScene} />
          <Route
            path={routePaths.wip}
            render={() => (
              <WorkInProgressScene
                {...verificationRequestsQueryResult}
                data={resultData}
                onFilterChange={setFilter}
              />
            )}
          />
          <Route
            path={routePaths.newProgramParticipant}
            component={NewProgramParticipantPage}
          />
          <Route path={routePaths.wipParticipant} component={WipParticipant} />
        </Switch>
      </View>
    </View>
  );
}

let styles = StyleSheet.create({
  root: {
    backgroundColor: colors.background.grey,
    flex: 1,
    maxHeight: '100vh',
    overflow: 'hidden',
  },
  header: {
    backgroundColor: colors.background.white,
    flexDirection: 'row',
    height: 80,
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    shadowColor: colors.background.shadow,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowRadius: 16,
    shadowOpacity: 0.04,
    elevation: 5,
    zIndex: 1,
  },
  row: {
    flexDirection: 'row',
    height: '100%',
  },
  contentWrapper: { flex: 1 },
  tab: {
    justifyContent: 'center',
    height: '100%',
    marginHorizontal: 12,
  },
  selectedTab: {
    borderBottomWidth: 2,
    borderBottomColor: colors.button.background.primary,
  },
  headerTitle: { alignItems: 'center', flexDirection: 'row' },
  logout: { alignItems: 'center', flexDirection: 'row' },
  logo: { width: 36, height: 36, marginRight: 16 },
});
